/**
 * AuthDialog — modal for sign in / sign up with Google, Apple, and email/password.
 * Same backdrop/dialog pattern as AiEnhanceDialog.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountStore } from '@/store/accountStore.ts';
import {
  signUp, signIn, signInWithGoogle, signInWithApple, upgradeAnonymousAccount,
} from '@/api/accountApi.ts';
import { WELCOME_CREDITS } from '@/ai/tokenCosts.ts';
import styles from './AuthDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  /** When true, shows "upgrade" flow for anonymous users who want to buy tokens */
  upgrade?: boolean;
}

export function AuthDialog({ open, onClose, upgrade }: Props) {
  const { t } = useTranslation('account');
  const setAccountUser = useAccountStore(s => s.setAccountUser);
  const refreshBalance = useAccountStore(s => s.refreshBalance);

  const [tab, setTab] = useState<'signin' | 'signup'>(upgrade ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    setError('');
    if (!email || !password) { setError(t('errorEmailPasswordRequired')); return; }
    if (tab === 'signup' && password !== confirmPassword) { setError(t('errorPasswordsDoNotMatch')); return; }
    if (tab === 'signup' && password.length < 6) { setError(t('errorPasswordTooShort')); return; }

    setLoading(true);
    try {
      let user;
      if (upgrade) {
        user = await upgradeAnonymousAccount(email, password);
      } else if (tab === 'signup') {
        user = await signUp(email, password);
      } else {
        user = await signIn(email, password);
      }
      setAccountUser(user);
      await refreshBalance();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorAuthFailed'));
    } finally {
      setLoading(false);
    }
  }, [email, password, confirmPassword, tab, upgrade, setAccountUser, refreshBalance, onClose, t]);

  const handleGoogle = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      // Redirect happens — no need to close dialog
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorGoogleSignInFailed'));
      setLoading(false);
    }
  }, [t]);

  const handleApple = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithApple();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorAppleSignInFailed'));
      setLoading(false);
    }
  }, [t]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.title}>
            {upgrade ? t('authDialogTitleCreateAccount') : t('authDialogTitleSignIn')}
          </span>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <div className={styles.body}>
          {/* OAuth buttons */}
          {!upgrade && (
            <>
              <div className={styles.oauthSection}>
                <button className={styles.oauthBtn} onClick={handleGoogle} disabled={loading}>
                  <svg className={styles.oauthIcon} viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t('continueWithGoogle')}
                </button>
                <button className={styles.oauthBtn} onClick={handleApple} disabled={loading}>
                  <svg className={styles.oauthIcon} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  {t('continueWithApple')}
                </button>
              </div>
              <div className={styles.divider}>{t('oauthDividerOr')}</div>
            </>
          )}

          {/* Tabs (sign in / sign up) */}
          {!upgrade && (
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${tab === 'signin' ? styles.tabActive : ''}`}
                onClick={() => { setTab('signin'); setError(''); }}
              >
                {t('signInTab')}
              </button>
              <button
                className={`${styles.tab} ${tab === 'signup' ? styles.tabActive : ''}`}
                onClick={() => { setTab('signup'); setError(''); }}
              >
                {t('signUpTab')}
              </button>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('emailLabel')}</label>
            <input
              className={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('emailPlaceholder')}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('passwordLabel')}</label>
            <input
              className={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t('passwordPlaceholder')}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          {(tab === 'signup' || upgrade) && (
            <div className={styles.fieldGroup}>
              <label className={styles.label}>{t('confirmPasswordLabel')}</label>
              <input
                className={styles.input}
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder={t('confirmPasswordPlaceholder')}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
            </div>
          )}

          {(tab === 'signup' || upgrade) && (
            <div className={styles.welcomeNote}>
              {t('welcomeCreditsNote', { credits: WELCOME_CREDITS.toLocaleString() })}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.secondaryBtn} onClick={onClose}>{t('cancelButton')}</button>
          <button
            className={styles.primaryBtn}
            onClick={handleSubmit}
            disabled={loading || !email || !password}
          >
            {loading ? t('loadingButton') : upgrade ? t('authDialogTitleCreateAccount') : tab === 'signup' ? t('signUpButton') : t('signInTab')}
          </button>
        </div>
      </div>
    </div>
  );
}
